import React, { FC } from "react";
import { observer } from "mobx-react";
import {
  CustomLabel,
  FlexContainer,
  Input,
  IOption,
  SingleSelect,
  ColorPicker as ColorPickerComponent,
} from "@mindmemobile/mm-shared-global";
import {
  BasicColors,
  CheckoutBrandingModel,
  ColorPickerElement,
} from "../../../shared/models";
import {
  CheckOutButtonStyle,
  CheckOutDefaultSegmentType,
  CheckOutPayButtonCaption,
} from "../../../shared/enums";
import {
  ButtonStyleSelector,
  DescriptionCell,
  LabelCell,
  SelectedColorButton,
} from "../../../shared/styles";

export type ColorPickerType =
  | "Primary"
  | "Tabs"
  | "Recommended"
  | "Page Background";
type IProps = {
  basicColors: BasicColors;
  onChange: (field: keyof CheckoutBrandingModel, value: BasicColors) => void;
};

const buttonOptions: IOption[] = Object.keys(CheckOutPayButtonCaption).map(
  (key) => {
    return {
      label:
        CheckOutPayButtonCaption[key as keyof typeof CheckOutPayButtonCaption],
      value: key,
    };
  }
);

const segmentOptions: IOption[] = Object.keys(CheckOutDefaultSegmentType).map(
  (key) => {
    return {
      label:
        CheckOutDefaultSegmentType[
          key as keyof typeof CheckOutDefaultSegmentType
        ],
      value: key,
    };
  }
);

const CheckoutBasicColorSection: FC<IProps> = ({ onChange, basicColors }) => {
  // const getColor = (field: CheckOutButtonStyle) => {
  //     const isSelected = field === basicColors.buttonStyleType;
  //     return isSelected ? 'red' : 'black';
  // };

  const singleSelect = (
    field: keyof BasicColors,
    selectedKey: string,
    className: string,
    options: IOption[],
    error: boolean,
    errorMessage: string
  ): React.ReactNode => {
    return (
      <SingleSelect
        selectedKey={selectedKey}
        hasError={error}
        searchable={false}
        nameKeys={["label"]}
        valueKeys={["value"]}
        options={[options || []]}
        errorMessage={errorMessage}
        onChange={(option: any) => handleInputChange(field, option.value)}
        className={className}
      />
    );
  };

  const textInput = (
    field: keyof BasicColors,
    type: string,
    value: string,
    placeHolder: string,
    className: string,
    error: boolean,
    errorMessage: string,
    maxLength?: number
  ) => {
    return (
      <Input
        type={"text"}
        value={value}
        error={error}
        errorMessage={errorMessage}
        className={`styled-input merchant-inputs ${className}`}
        onChange={(e) => handleInputChange(field, e.target.value)}
        placeholder={placeHolder}
        maxLength={maxLength}
      />
    );
  };

  const handleColorPickerChange = (
    pickerType: ColorPickerType,
    key: keyof ColorPickerElement,
    value: any
  ) => {
    const updatedColorPickers = basicColors.colorPickers.map((picker) => {
      if (picker.name === pickerType) {
        return new ColorPickerElement({
          ...picker,
          [key]: value,
        });
      }
      return picker;
    });
    const updatedBasicColors = {
      ...basicColors,
      colorPickers: updatedColorPickers,
    };
    onChange("basicColors", new BasicColors(updatedBasicColors));
  };

  const handleInputChange = (
    field: keyof BasicColors,
    value: string | boolean
  ) => {
    const updatedBasicColors = new BasicColors({
      ...basicColors,
      [field]: value,
    });
    onChange("basicColors", updatedBasicColors);
  };

  const renderColorPickerElement = (
    pickerType: ColorPickerType,
    label: string
  ) => {
    const colorPicker = basicColors.colorPickers.find(
      (picker) => picker.name === pickerType
    );
    return (
      <div key={pickerType} className="colored-block-div">
        {colorPicker && (
          <FlexContainer
            spacing={10}
            alignItems="center"
            className="position-relative"
          >
            <SelectedColorButton
              heightWidth="24px"
              padding="3px"
              borderRadius="2px"
              onClick={() =>
                handleColorPickerChange(
                  pickerType,
                  "showColorPicker",
                  !colorPicker.showColorPicker
                )
              }
              color={colorPicker.color?.rgba}
              className="color-picker-gallery"
            >
              <div />
            </SelectedColorButton>

            {colorPicker.showColorPicker && (
              <ColorPickerComponent
                color={colorPicker.deserializeColor()}
                onChange={(color) =>
                  handleColorPickerChange(pickerType, "color", color)
                }
                onHide={() =>
                  handleColorPickerChange(pickerType, "showColorPicker", false)
                }
                className="checkout-color-picker"
              />
            )}
            <CustomLabel>{label}</CustomLabel>
          </FlexContainer>
        )}
      </div>
    );
  };

  const renderBasicColorsContent = () => {
    return (
      <>
        <FlexContainer>
          <LabelCell>
            <CustomLabel className="standard-lh">Main</CustomLabel>
          </LabelCell>
          <DescriptionCell>
            <FlexContainer
              className="position-relative"
              fxWrap="wrap"
              spacing={10}
            >
              {renderColorPickerElement("Primary", "Primary")}
              {renderColorPickerElement("Tabs", "Tabs")}
              {renderColorPickerElement("Recommended", "Recommended")}
            </FlexContainer>
          </DescriptionCell>
        </FlexContainer>
        <FlexContainer>
          <LabelCell>
            <CustomLabel className="standard-lh"> Background</CustomLabel>
          </LabelCell>
          <DescriptionCell>
            {renderColorPickerElement("Page Background", "Page Background")}
          </DescriptionCell>
        </FlexContainer>
        <FlexContainer>
          <LabelCell>
            <CustomLabel className="standard-lh"> Button Styles</CustomLabel>
          </LabelCell>
          <DescriptionCell>
            <FlexContainer spacing={20}>
              <ButtonStyleSelector
                pillShape
                isActive={
                  basicColors.buttonStyleType === CheckOutButtonStyle.Round
                }
                onClick={() =>
                  handleInputChange(
                    "buttonStyleType",
                    CheckOutButtonStyle.Round
                  )
                }
              />
              <ButtonStyleSelector
                roundedRect
                isActive={
                  basicColors.buttonStyleType ===
                  CheckOutButtonStyle.SquareRoundCorner
                }
                onClick={() =>
                  handleInputChange(
                    "buttonStyleType",
                    CheckOutButtonStyle.SquareRoundCorner
                  )
                }
              />
              <ButtonStyleSelector
                isActive={
                  basicColors.buttonStyleType === CheckOutButtonStyle.Square
                }
                onClick={() =>
                  handleInputChange(
                    "buttonStyleType",
                    CheckOutButtonStyle.Square
                  )
                }
              />
            </FlexContainer>
          </DescriptionCell>
        </FlexContainer>
        <FlexContainer>
          <LabelCell>
            <CustomLabel>Button Caption</CustomLabel>
          </LabelCell>
          <DescriptionCell>
            <FlexContainer alignItems="center" spacing={15} fxWrap="wrap">
              {singleSelect(
                "payButtonType",
                basicColors.payButtonType,
                "dpdn-no-separator btn-caption-dpdn",
                buttonOptions,
                basicColors.hasError("payButtonType"),
                ""
              )}
              {basicColors.payButtonType === CheckOutPayButtonCaption.Custom &&
                textInput(
                  "buttonCustomCaption",
                  "text",
                  basicColors.buttonCustomCaption,
                  "Enter caption",
                  "custom-caption-input",
                  basicColors.hasError("buttonCustomCaption"),
                  "",
                  50
                )}
            </FlexContainer>
          </DescriptionCell>
        </FlexContainer>
        <FlexContainer>
          <LabelCell>
            <CustomLabel>Default Segment</CustomLabel>
          </LabelCell>
          <DescriptionCell>
            {singleSelect(
              "defaultSegment",
              basicColors.defaultSegment,
              "dpdn-no-separator checkout-segment-dpdn",
              segmentOptions,
              basicColors.hasError("defaultSegment"),
              ""
            )}
          </DescriptionCell>
        </FlexContainer>
      </>
    );
  };

  return <>{renderBasicColorsContent()}</>;
};

export default observer(CheckoutBasicColorSection);
